import React, { useEffect, useState } from 'react';
import { FiDollarSign, FiCheck, FiRefreshCw, FiAlertCircle, FiX } from 'react-icons/fi';
import { supabase } from '@/integrations/supabase/client';

type Row = {
  affiliate_user_id: string;
  affiliate_email: string | null;
  payout_method: string | null;
  payout_destination: string | null;
  period_month: string;
  invoices_count: number;
  total_pending_usd: number;
};

const fmtUsd = (n: number) => `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const AffiliatePayoutsPanel: React.FC = () => {
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [payingRow, setPayingRow] = useState<Row | null>(null);
  const [txHash, setTxHash] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const { data, error: e } = await supabase.rpc('get_admin_pending_commissions');
      if (e) throw e;
      setRows((data as Row[]) || []);
    } catch (err: any) {
      setError(err.message || 'Failed to load pending commissions');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const openPay = (row: Row) => {
    setPayingRow(row); setTxHash(''); setNotes(''); setError(null);
  };

  const submitPay = async () => {
    if (!payingRow || !payingRow.payout_method || !payingRow.payout_destination) {
      setError('This affiliate has no payout method configured. Ask them to complete onboarding.');
      return;
    }
    if (txHash.trim().length < 3) {
      setError('Tx hash / transaction ID required (min 3 chars).');
      return;
    }
    setSubmitting(true); setError(null);
    try {
      const { data, error: e } = await supabase.rpc('mark_payout_paid', {
        p_affiliate_id: payingRow.affiliate_user_id,
        p_period_month: payingRow.period_month,
        p_payout_method: payingRow.payout_method,
        p_payout_destination: payingRow.payout_destination,
        p_tx_hash: txHash.trim(),
        p_notes: notes.trim() || null,
      });
      if (e) throw e;
      if (!(data as any)?.success) throw new Error((data as any)?.reason || 'failed');
      setPayingRow(null);
      await load();
    } catch (err: any) {
      setError(err.message || 'Failed to mark paid');
    } finally { setSubmitting(false); }
  };

  const totalPending = rows.reduce((s, r) => s + Number(r.total_pending_usd || 0), 0);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-white flex items-center gap-2"><FiDollarSign className="text-cyan-400" /> Affiliate Payouts</h2>
          <p className="text-gray-400 text-sm mt-1">Pending commissions to settle, grouped by affiliate and month.</p>
        </div>
        <button onClick={load} className="px-3 py-2 bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg text-sm text-gray-300 flex items-center gap-2">
          <FiRefreshCw className="w-4 h-4" /> Refresh
        </button>
      </div>

      {error && !payingRow && (
        <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3 text-red-300 text-sm flex items-center gap-2">
          <FiAlertCircle /> {error}
        </div>
      )}

      {/* Summary */}
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-white/5 border border-white/10 rounded-xl p-4">
          <div className="text-xs uppercase tracking-wider text-gray-500 mb-1">Pending batches</div>
          <div className="text-2xl font-bold text-white tabular-nums">{rows.length}</div>
        </div>
        <div className="bg-white/5 border border-white/10 rounded-xl p-4">
          <div className="text-xs uppercase tracking-wider text-gray-500 mb-1">Affiliates owed</div>
          <div className="text-2xl font-bold text-white tabular-nums">{new Set(rows.map(r => r.affiliate_user_id)).size}</div>
        </div>
        <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-4">
          <div className="text-xs uppercase tracking-wider text-amber-400 mb-1">Total to pay</div>
          <div className="text-2xl font-bold text-amber-300 tabular-nums">{fmtUsd(totalPending)}</div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white/5 border border-white/10 rounded-xl p-4">
        {loading ? (
          <div className="py-10 text-center text-gray-500"><FiRefreshCw className="animate-spin mx-auto mb-2" /> Loading…</div>
        ) : rows.length === 0 ? (
          <div className="py-8 text-center text-sm text-gray-500">No pending commissions. Either no affiliate has accrued earnings yet, or everything has been paid.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-gray-500 border-b border-white/5">
                  <th className="py-2">Affiliate</th>
                  <th className="py-2">Method</th>
                  <th className="py-2">Destination</th>
                  <th className="py-2">Period</th>
                  <th className="py-2 text-right">Invoices</th>
                  <th className="py-2 text-right">Amount</th>
                  <th className="py-2 text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={i} className="border-b border-white/5 hover:bg-white/[0.03]">
                    <td className="py-2 text-gray-200">{r.affiliate_email || <span className="text-gray-600">—</span>}</td>
                    <td className="py-2 uppercase text-xs text-gray-400">{r.payout_method || <span className="text-amber-400">unset</span>}</td>
                    <td className="py-2 text-xs text-gray-400 font-mono break-all max-w-[16rem]">{r.payout_destination || <span className="text-amber-400">unset</span>}</td>
                    <td className="py-2 text-gray-300 text-xs">{r.period_month}</td>
                    <td className="py-2 text-right text-gray-400 tabular-nums">{r.invoices_count}</td>
                    <td className="py-2 text-right text-emerald-400 font-medium tabular-nums">{fmtUsd(r.total_pending_usd)}</td>
                    <td className="py-2 text-right">
                      <button onClick={() => openPay(r)} disabled={!r.payout_method || !r.payout_destination}
                        className="px-3 py-1.5 bg-emerald-500/20 hover:bg-emerald-500/30 disabled:opacity-40 disabled:cursor-not-allowed border border-emerald-500/30 text-emerald-300 rounded-md text-xs font-medium">
                        Mark paid
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pay modal */}
      {payingRow && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => !submitting && setPayingRow(null)}>
          <div className="bg-gray-900 border border-white/10 rounded-2xl p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-white">Mark batch as paid</h3>
              <button onClick={() => setPayingRow(null)} className="text-gray-500 hover:text-white"><FiX /></button>
            </div>

            <div className="bg-white/5 border border-white/10 rounded-lg p-3 text-sm space-y-1 mb-4">
              <div className="flex justify-between"><span className="text-gray-400">Affiliate</span><span className="text-white">{payingRow.affiliate_email}</span></div>
              <div className="flex justify-between"><span className="text-gray-400">Period</span><span className="text-white">{payingRow.period_month}</span></div>
              <div className="flex justify-between"><span className="text-gray-400">Method</span><span className="text-white uppercase text-xs">{payingRow.payout_method}</span></div>
              <div className="flex justify-between"><span className="text-gray-400">Destination</span><span className="text-white font-mono text-xs break-all ml-2">{payingRow.payout_destination}</span></div>
              <div className="flex justify-between border-t border-white/10 mt-2 pt-2"><span className="text-gray-400">Amount</span><span className="text-emerald-400 font-bold">{fmtUsd(payingRow.total_pending_usd)}</span></div>
            </div>

            {error && (
              <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3 text-red-300 text-sm flex items-center gap-2 mb-3">
                <FiAlertCircle /> {error}
              </div>
            )}

            <label className="block text-sm font-medium text-white mb-1">Transaction hash / ID *</label>
            <input type="text" value={txHash} onChange={(e) => setTxHash(e.target.value)} placeholder={payingRow.payout_method === 'usdt' ? '0xabc...' : 'PayPal txn id'}
              className="w-full px-3 py-2 bg-gray-800 border border-white/10 rounded-lg text-white text-sm mb-3" />

            <label className="block text-sm font-medium text-white mb-1">Notes (optional)</label>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2}
              className="w-full px-3 py-2 bg-gray-800 border border-white/10 rounded-lg text-white text-sm mb-4" />

            <div className="flex gap-2">
              <button onClick={() => setPayingRow(null)} disabled={submitting} className="flex-1 px-4 py-2 bg-white/5 hover:bg-white/10 border border-white/10 text-gray-300 rounded-lg text-sm">Cancel</button>
              <button onClick={submitPay} disabled={submitting} className="flex-1 px-4 py-2 bg-emerald-500/20 hover:bg-emerald-500/30 border border-emerald-500/30 text-emerald-300 rounded-lg text-sm font-medium disabled:opacity-50 flex items-center justify-center gap-2">
                <FiCheck /> {submitting ? 'Saving…' : 'Confirm payout'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AffiliatePayoutsPanel;
